import React, { useEffect, useState, useMemo, memo, useCallback } from "react";
import { GetViewErrorOnlinePolicies } from "../../../Service/SearchPolicy";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Stack,
  Button,
  Collapse,
  ButtonBase,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoadingImage from "../../../UiComponents/CommonLoader/LoadingImage";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ImageIcon from "@mui/icons-material/Image";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import moment from "moment";
import { PartTag, PartFilterChips, matchesPart } from "./components/PartTag";
import SegmentedFilter from "./components/SegmentedFilter";
import { friendlyError, errorTypeLabel, stageLabel } from "./utils/friendlyError";

// Constants
const ERROR_MESSAGES = {
  NO_RECORDS: "No issues found for this policy.",
  LOAD_FAILED: "Failed to load policy data",
};

// Redesigned Error Log Views with Brand Colors
const COLORS = {
  navy: "#1E3A5F",
  gold: "#D4AF37",
  lightNavy: "#2E5A8F",
  bodyBg: "#F5F7FB",
  errorRed: "#d32f2f",
  infoBlue: "#1976d2",
  textGray: "#64748B",
};

/**
 * Which kind of failure is this?
 *
 * Grouped by what an operator would DO about it, not by the automation's own
 * stage names — those are too fine-grained to filter by (there are a dozen) and
 * they say where the code was, not what went wrong.
 *
 * Read off errorType and stage where they are set, and off the message where
 * they are not: the older entries on a policy predate those fields.
 */
const ERROR_KINDS = [
  {
    key: "brisk",
    label: "Brisk certificate",
    color: "#6941C6",
    test: (t) => /brisk|cpa|rsa certificate/.test(t),
  },
  {
    key: "login",
    label: "Login",
    color: "#b3261e",
    test: (t) => /login|credential|authentication|password|sign.?in/.test(t),
  },
  {
    key: "kyc",
    label: "KYC",
    color: "#ed6c02",
    test: (t) => /kyc|aadhaar|aadhar|digilocker|no record found/.test(t),
  },
  {
    key: "payment",
    label: "Payment",
    color: "#0288d1",
    test: (t) => /payment|razorpay|wallet|qr/.test(t),
  },
  {
    key: "form",
    label: "Form data",
    color: "#2e7d32",
    test: (t) => /field|dropdown|not on the page|validation|required|idv|rto|pin ?code/.test(t),
  },
];

const classifyError = (errorData) => {
  const text = [
    errorData?.errorType,
    errorData?.stage,
    errorData?.errorMessage,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const kind = ERROR_KINDS.find((k) => k.test(text));
  return kind ? kind.key : "other";
};

/**
 * The issues newest first (the owner, 2026-10-27; the server keeps them oldest first), each with `issueNo` = its place
 * in time order (1 = the first one), so "Issue 11" stays 11 whatever the filters show. A log without a time counts as
 * the oldest; equal times keep the server's order.
 */
export const numberIssues = (logs) => {
  const timeOf = (e) => {
    const t = e && e.timestamp ? new Date(e.timestamp).getTime() : NaN;
    return Number.isFinite(t) ? t : -Infinity;
  };
  const oldestFirst = (logs || [])
    .filter(Boolean)
    .map((entry, at) => ({ entry, at, t: timeOf(entry) }))
    .sort((a, b) => (a.t === b.t ? a.at - b.at : a.t < b.t ? -1 : 1));
  return oldestFirst.map(({ entry }, i) => ({ ...entry, issueNo: i + 1 })).reverse();
};

const timeText = (timestamp) => (timestamp ? moment(timestamp).format("DD MMM YYYY, hh:mm A") : "Time not recorded");

const ViewOnlinePolicyErrorLogs = () => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const [policyData, setPolicyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  // OD / TP / PA: the job's parts, and which one the list is narrowed to.
  const [parts, setParts] = useState([]);
  const [partFilter, setPartFilter] = useState("all");

  // Newest first, each with its number in time order (numberIssues).
  const issues = useMemo(() => numberIssues(policyData), [policyData]);
  // Earlier issues folded into rows: the ones opened by a click.
  const [openIssues, setOpenIssues] = useState(() => new Set());
  const toggleIssue = useCallback(
    (no) =>
      setOpenIssues((now) => {
        const next = new Set(now);
        if (next.has(no)) next.delete(no);
        else next.add(no);
        return next;
      }),
    []
  );

  // Errors of the chosen part (policy-wide ones stay under every part).
  const partErrors = useMemo(
    () => issues.filter((e) => matchesPart(e, partFilter)),
    [issues, partFilter]
  );

  // One chip per kind actually present, newest counts included, plus "All".
  // Built from the data rather than a fixed list so a policy never shows a
  // filter that would come back empty.
  const filters = useMemo(() => {
    const counts = new Map();
    for (const e of partErrors) {
      const k = classifyError(e);
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    const present = [...ERROR_KINDS, { key: "other", label: "Other", color: "#667085" }]
      .filter((k) => counts.get(k.key))
      .map((k) => ({ ...k, count: counts.get(k.key) }));

    return present.length
      ? [{ key: "all", label: "All", color: "#1E3A5F", count: partErrors.length }, ...present]
      : [];
  }, [partErrors]);

  const visibleErrors = useMemo(
    () =>
      activeFilter === "all"
        ? partErrors
        : partErrors.filter((e) => classifyError(e) === activeFilter),
    [partErrors, activeFilter]
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!policyId) {
      setError("Policy ID is required");
      setLoading(false);
      return;
    }

    const fetchPolicyData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await GetViewErrorOnlinePolicies({ policyId });
        const errorLogs = response?.data?.errorLogs || [];
        setPolicyData(errorLogs);
        setParts(response?.data?.parts || []);
      } catch (err) {
        setError(err?.message || ERROR_MESSAGES.LOAD_FAILED);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicyData();
  }, [policyId]);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
      <CircularProgress sx={{ color: COLORS.navy }} />
    </Box>
  );

  if (error) return (
    <Box p={3}>
      <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
    </Box>
  );

  const newest = issues[0] || null;
  const [first, ...earlier] = visibleErrors;
  // A filter is shown only when it narrows the list: two parts / two kinds that have issues ("All 5 · Login 5" did not).
  const partCounts = {
    all: issues.length,
    ...Object.fromEntries(parts.map((p) => [p.key, issues.filter((e) => matchesPart(e, p.key)).length])),
  };
  const showPartFilter = parts.filter((p) => partCounts[p.key] > 0).length > 1;
  const showKindFilter = filters.length > 2; // "All" + at least two kinds

  return (
    <Box sx={{ p: { xs: 0.5, md: 1 }, backgroundColor: "transparent" }}>
      {/* The page title and Back to List are in OnlinePolicy.js */}
      {issues.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {ERROR_MESSAGES.NO_RECORDS}
        </Alert>
      ) : (
        <>
          {/* How many, when the latest was, and the filters. One chip per KIND of failure with its count: a policy
              retried a few times stacks up a dozen entries and they are rarely the same problem. */}
          <Box
            data-testid="issues-summary"
            sx={{
              mb: 2,
              px: { xs: 1.5, md: 2 },
              py: 1.5,
              border: "1px solid #EAECF0",
              borderRadius: "12px",
              backgroundColor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              columnGap: 3,
              rowGap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: "10px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#FEF3F2", color: "#D92D20" }}>
                <ReportProblemOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "15px", fontWeight: 800, color: COLORS.navy, lineHeight: 1.3 }}>
                  {issues.length} issue{issues.length === 1 ? "" : "s"}{" "}
                  <Box component="span" sx={{ fontWeight: 500, color: "#475467" }}>
                    on this policy
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: "12.5px", color: COLORS.textGray, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 13 }} /> Latest {timeText(newest && newest.timestamp)}
                </Typography>
              </Box>
            </Box>
            {showPartFilter || showKindFilter ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 2.5, rowGap: 1 }}>
                {showPartFilter ? (
                  <FilterGroup label="Part">
                    <PartFilterChips
                      parts={parts}
                      value={partFilter}
                      onChange={(key) => {
                        setPartFilter(key);
                        setActiveFilter("all"); // the kind chips are rebuilt for this part
                      }}
                      counts={partCounts}
                      sx={{ mb: 0 }}
                    />
                  </FilterGroup>
                ) : null}
                {showKindFilter ? (
                  <FilterGroup label="Type">
                    <SegmentedFilter options={filters} value={activeFilter} onChange={setActiveFilter} testId="error-kind-filter" sx={{ mb: 0 }} />
                  </FilterGroup>
                ) : null}
              </Box>
            ) : null}
          </Box>

          {!first ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No issues of this kind on this policy.
            </Alert>
          ) : (
            <>
              {/* The newest issue shown (the latest of the policy, or the newest of the chosen kind) open in full */}
              <ErrorCard errorData={first} parts={parts} latest={!!newest && first.issueNo === newest.issueNo} />
              {earlier.length > 0 && (
                <>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#667085", mt: 2.5, mb: 1 }}>
                    Earlier issues
                  </Typography>
                  <Stack spacing={0.75} data-testid="earlier-issues">
                    {earlier.map((errorData) => (
                      <IssueRow
                        key={`issue-${errorData.issueNo}`}
                        errorData={errorData}
                        parts={parts}
                        open={openIssues.has(errorData.issueNo)}
                        onToggle={toggleIssue}
                      />
                    ))}
                  </Stack>
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

/** A filter with its caption on the same line ("Part [All | OD | TP]"). */
const FilterGroup = ({ label, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
    <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#667085" }}>{label}</Typography>
    {children}
  </Box>
);

/** What happened, in plain words, for one stored issue (utils/friendlyError.js). */
const useFriendly = (errorData) => {
  const { errorMessage = "No error message available", errorType = "Unknown", stage = "Unknown", errorCode } = errorData || {};
  return useMemo(() => friendlyError(errorMessage, { errorType, stage, errorCode }), [errorMessage, errorType, stage, errorCode]);
};

/** "Try 2" in the navy pill. */
const TryChip = ({ attemptNumber = 1 }) => (
  <Chip
    label={`Try ${attemptNumber}`}
    size="small"
    sx={{ backgroundColor: COLORS.navy, color: "white", fontWeight: 700, height: 20, fontSize: "0.65rem" }}
  />
);

/** "15 Oct 2026, 10:17 AM · Step - Quote · While logging in / filling the form" */
const IssueMeta = ({ errorData }) => {
  const { timestamp, stage = "Unknown", errorType = "Unknown" } = errorData || {};
  return (
    <Typography variant="caption" sx={{ color: COLORS.textGray, display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 0.75, rowGap: 0.25 }}>
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <AccessTimeIcon sx={{ fontSize: 12 }} /> {timeText(timestamp)}
      </Box>
      <span>·</span>
      <span>
        Step <b>{stageLabel(stage)}</b>
      </span>
      <span>·</span>
      <span>{errorTypeLabel(errorType)}</span>
    </Typography>
  );
};

/** An earlier issue: one line (number, time, part, try, what happened) that opens the full issue below it. */
const IssueRow = memo(({ errorData, parts, open, onToggle }) => {
  const friendly = useFriendly(errorData);
  const { issueNo, timestamp, part, attemptNumber = 1, screenshotUrl } = errorData;
  return (
    <Paper elevation={0} data-testid={`issue-row-${issueNo}`} sx={{ border: "1px solid #EAECF0", borderRadius: "10px", overflow: "hidden", backgroundColor: "#fff" }}>
      <ButtonBase
        onClick={() => onToggle(issueNo)}
        aria-expanded={open}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          textAlign: "left",
          px: { xs: 1.25, md: 1.5 },
          py: 1.1,
          display: "flex",
          alignItems: "center",
          flexWrap: { xs: "wrap", md: "nowrap" },
          columnGap: 1,
          rowGap: 0.5,
          "&:hover": { backgroundColor: "#F9FAFB" },
        }}
      >
        <Typography sx={{ fontWeight: 800, color: COLORS.textGray, fontSize: "12.5px", minWidth: 30 }}>#{issueNo}</Typography>
        <Typography sx={{ color: COLORS.textGray, fontSize: "12px", width: { md: 150 }, flexShrink: 0 }}>{timeText(timestamp)}</Typography>
        <PartTag partKey={part} parts={parts} />
        <TryChip attemptNumber={attemptNumber} />
        <Typography
          sx={{
            flex: 1,
            minWidth: { xs: "100%", md: 0 },
            order: { xs: 5, md: 0 },
            fontWeight: 600,
            color: COLORS.navy,
            fontSize: "13px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: { md: "nowrap" },
          }}
        >
          {friendly.title}
        </Typography>
        <Box sx={{ ml: { xs: "auto", md: 0 }, display: "flex", alignItems: "center", gap: 0.5, color: COLORS.textGray }}>
          {screenshotUrl ? <ImageIcon titleAccess="Has a screenshot" sx={{ fontSize: 16 }} /> : null}
          <ExpandMoreIcon sx={{ fontSize: 20, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }} />
        </Box>
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ borderTop: "1px solid #EAECF0", p: { xs: 1.25, md: 2 } }}>
          <IssueBody errorData={errorData} friendly={friendly} showMeta />
        </Box>
      </Collapse>
    </Paper>
  );
});
IssueRow.displayName = "IssueRow";

/** The latest issue, open in full: its header (number, Latest, part, try, time and step), then the body. */
const ErrorCard = memo(({ errorData, parts, latest }) => {
  const friendly = useFriendly(errorData);
  const { issueNo, part, attemptNumber = 1 } = errorData || {};
  return (
    <Card
      elevation={0}
      data-testid={`issue-card-${issueNo}`}
      sx={{
        borderRadius: "12px",
        border: "1px solid #E2E8F0",
        borderLeft: `5px solid ${latest ? "#F04438" : "#CBD5E1"}`,
        backgroundColor: "white",
        boxShadow: latest ? "0 4px 12px rgba(240, 68, 56, 0.08)" : "none",
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 }, "&:last-child": { pb: { xs: 1.5, md: 2 } } }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.75 }}>
          <Box sx={{ backgroundColor: "#FEF3F2", color: "#D92D20", borderRadius: "8px", p: 0.75, display: "flex", flexShrink: 0 }}>
            <ReportProblemOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.navy, lineHeight: 1.2 }}>
                Issue {issueNo}
              </Typography>
              {latest ? (
                <Chip label="Latest" size="small" data-testid="latest-badge" sx={{ backgroundColor: "#F04438", color: "#fff", fontWeight: 700, height: 20, fontSize: "0.65rem" }} />
              ) : null}
              <PartTag partKey={part} parts={parts} />
              <TryChip attemptNumber={attemptNumber} />
            </Box>
            <Box sx={{ mt: 0.3 }}>
              <IssueMeta errorData={errorData} />
            </Box>
          </Box>
        </Stack>
        <IssueBody errorData={errorData} friendly={friendly} />
      </CardContent>
    </Card>
  );
});
ErrorCard.displayName = "ErrorCard";

/** Screenshot on the left; what happened, what to do and the details for support on the right (stacked on a phone). */
const IssueBody = ({ errorData, friendly, showMeta = false }) => {
  const { attemptNumber = 1, errorStack, screenshotUrl, errorCode } = errorData || {};
  const openScreenshot = () => {
    if (screenshotUrl) window.open(screenshotUrl, "_blank", "noopener,noreferrer");
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4.5}>
        {screenshotUrl ? (
          <Paper
            elevation={0}
            onClick={openScreenshot}
            sx={{ p: 0.75, borderRadius: "10px", border: "1px solid #E2E8F0", backgroundColor: "#F8FAFC", cursor: "pointer", "&:hover img": { transform: "scale(1.02)" } }}
          >
            {/* Full-resolution S3 captures take seconds on a slow line; LoadingImage holds the space and spins meanwhile. */}
            <Box sx={{ height: { xs: 200, md: 220 }, overflow: "hidden", borderRadius: "8px", backgroundColor: "#EDF2F7" }}>
              <LoadingImage
                src={screenshotUrl}
                alt={`Try ${attemptNumber} screenshot`}
                height="100%"
                sx={{ backgroundColor: "transparent" }}
                imgSx={{ transition: "transform 0.3s ease-out" }}
              />
            </Box>
            <Box sx={{ textAlign: "center", mt: 0.75, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, color: COLORS.navy }}>
              <ImageIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.7rem" }}>
                Open screenshot
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Box sx={{ height: { xs: 90, md: "100%" }, minHeight: { md: 120 }, display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #CBD5E1", borderRadius: "10px" }}>
            <Typography variant="body2" sx={{ color: COLORS.textGray, fontSize: "0.8rem" }}>
              No screenshot
            </Typography>
          </Box>
        )}
      </Grid>

      <Grid item xs={12} md={7.5}>
        {showMeta ? (
          <Box sx={{ mb: 1.25 }}>
            <IssueMeta errorData={errorData} />
          </Box>
        ) : null}
        <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textGray, textTransform: "uppercase", letterSpacing: 1, mb: 0.5, display: "block" }}>
          What happened
        </Typography>
        {/* overflowWrap: a long portal address in the message must wrap, not run out of the box on a phone */}
        <Paper elevation={0} sx={{ px: 1.75, py: 1.1, backgroundColor: "#FFF5F5", border: "1px solid #FED7D7", borderRadius: "8px", mb: 1.25, overflowWrap: "anywhere" }}>
          <Typography sx={{ color: "#9B2C2C", fontWeight: 700, lineHeight: 1.4, fontSize: "0.875rem" }}>{friendly.title}</Typography>
          {friendly.message && (
            <Typography variant="body2" sx={{ color: "#C53030", fontWeight: 500, lineHeight: 1.45, fontSize: "0.8rem", mt: 0.4 }}>
              {friendly.message}
            </Typography>
          )}
        </Paper>
        {friendly.action && (
          <Paper elevation={0} data-testid="error-action" sx={{ px: 1.75, py: 1, backgroundColor: "#F0F7FF", border: "1px solid #C9DDF7", borderRadius: "8px", mb: 1.25, overflowWrap: "anywhere" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.infoBlue, textTransform: "uppercase", letterSpacing: 1, display: "block" }}>
              What to do
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.navy, lineHeight: 1.45, fontSize: "0.8rem", mt: 0.3 }}>
              {friendly.action}
            </Typography>
          </Paper>
        )}

        {/* Details for support: the stored message (and stack) for whoever investigates, folded */}
        {(errorStack || friendly.technical) && (
          <Accordion
            elevation={0}
            disableGutters
            sx={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "8px !important", "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 38, "& .MuiAccordionSummary-content": { my: 0.5 } }}>
              <Box display="flex" alignItems="center" gap={1}>
                <SupportAgentOutlinedIcon sx={{ color: COLORS.navy, fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.navy, fontSize: "0.75rem" }}>
                  Details for support
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Paper elevation={0} sx={{ p: 1.2, backgroundColor: "#1E293B", borderRadius: 1.2, maxHeight: 140, overflow: "auto" }}>
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{ color: "#94A3B8", fontFamily: "'Fira Code', 'Monaco', 'Cascadia Code', monospace", fontSize: "0.65rem", margin: 0, whiteSpace: "pre-wrap" }}
                >
                  {[friendly.technical, errorCode ? `Code ${errorCode}` : "", errorStack].filter(Boolean).join("\n\n")}
                </Typography>
              </Paper>
            </AccordionDetails>
          </Accordion>
        )}
      </Grid>
    </Grid>
  );
};

export default ViewOnlinePolicyErrorLogs;
